/**
 * Session Recovery Utility
 * 
 * Handles restoring user sessions after returning from external payment flows
 * where the user might lose their authentication state.
 */
import toast from 'react-hot-toast';

interface StoredSessionData {
  userId: string;
  userEmail: string | null;
  plan: string;
  timestamp: number;
  returnTo: string;
}

/**
 * Check if the current URL indicates a return from payment
 * that might need session restoration
 */
export const checkForPaymentReturn = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('restore_session') || urlParams.has('subscription');
};

/**
 * Simplified session recovery that focuses on payment success tracking
 * Returns a promise that resolves to true if recovery was attempted
 */
export const attemptSessionRecovery = async (): Promise<boolean> => {
  try {
    console.log('Attempting simplified session recovery after payment');
    
    // Mark that payment was successful
    markSuccessfulPayment();
    
    // Check if we have stored session data for cleanup
    const sessionDataString = localStorage.getItem('wigconnection_payment_session');
    if (sessionDataString) {
      try {
        const sessionData = JSON.parse(sessionDataString) as StoredSessionData;
        console.log('Found stored session data for user:', sessionData.userId);
        
        // Clean up old session data
        localStorage.removeItem('wigconnection_payment_session');
      } catch (parseError) {
        console.warn('Error parsing stored session data:', parseError);
        localStorage.removeItem('wigconnection_payment_session');
      }
    }
    
    // Set success indicators with current timestamp
    const now = new Date().getTime();
    localStorage.setItem('subscription_success', 'true');
    localStorage.setItem('subscription_success_detected', 'true');
    localStorage.setItem('subscription_success_time', now.toString());
    localStorage.setItem('subscription_success_timestamp', now.toString());
    
    console.log('Payment success indicators set in localStorage');
    return true;
  } catch (error) {
    console.error('Error during simplified session recovery:', error);
    return false;
  }
};

/**
 * Mark the current session as having just completed payment
 * Sets multiple localStorage keys for compatibility with different parts of the app
 */
export const markSuccessfulPayment = (): void => {
  const now = new Date().getTime();
  
  // Set all possible keys that different parts of the app might check
  localStorage.setItem('subscription_success', 'true');
  localStorage.setItem('subscription_success_detected', 'true');
  localStorage.setItem('subscription_success_time', now.toString());
  localStorage.setItem('subscription_success_timestamp', now.toString());
  
  // Also add URL parameter for immediate detection
  const url = new URL(window.location.href);
  url.searchParams.set('payment_complete', 'true');
  url.searchParams.set('subscription', 'success');
  
  // Update the URL without triggering a page reload
  window.history.replaceState({}, '', url.toString());
  
  console.log('Payment success marked in localStorage and URL parameters');
};
